import React, { useState, useEffect } from 'react';
import { Table, Segment, Header, Loader, Message, Label, Statistic } from 'semantic-ui-react';
import axiosInstance from '../../lib/auth/axiosApi';

const MonarchDebt = () => {
  const [debtAccounts, setDebtAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDebtAccounts();
  }, []);

  const fetchDebtAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/monarch/debt/');
      setDebtAccounts(response.data.accounts || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch debt accounts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(value));
  };

  const getDebtTypeColor = (type) => {
    const typeMap = {
      'credit': 'orange',
      'loan': 'red',
      'mortgage': 'purple',
      'auto': 'blue',
      'student': 'green',
      'other': 'grey'
    };
    return typeMap[type.toLowerCase()] || 'red';
  };

  if (loading) {
    return (
      <Segment style={{ minHeight: '200px' }}>
        <Loader active>Loading debt accounts...</Loader>
      </Segment>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Error</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  const totalDebt = debtAccounts.reduce((sum, acc) => sum + Math.abs(acc.balance), 0);

  return (
    <div>
      <Segment textAlign="center">
        <Header as="h2">Total Debt</Header>
        <Statistic color="red" size="large">
          <Statistic.Value>{formatCurrency(totalDebt)}</Statistic.Value>
          <Statistic.Label>Across {debtAccounts.length} Accounts</Statistic.Label>
        </Statistic>
      </Segment>

      <Header as="h3">Debt Accounts</Header>
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Account Name</Table.HeaderCell>
            <Table.HeaderCell>Institution</Table.HeaderCell>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Balance Owed</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {debtAccounts.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan="5" textAlign="center">
                <Message info>
                  <Message.Content>
                    Congratulations! You have no debt accounts.
                  </Message.Content>
                </Message>
              </Table.Cell>
            </Table.Row>
          ) : (
            debtAccounts.map((account) => (
              <Table.Row key={account.id}>
                <Table.Cell>{account.name}</Table.Cell>
                <Table.Cell>{account.institution || 'Unknown'}</Table.Cell>
                <Table.Cell>
                  <Label color={getDebtTypeColor(account.type)} size="small">
                    {account.type}
                  </Label>
                  {account.subtype && account.subtype !== account.type && (
                    <Label basic size="small" style={{ marginLeft: '5px' }}>
                      {account.subtype}
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <span style={{ color: 'red', fontWeight: 'bold' }}>
                    {formatCurrency(account.balance)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {account.isHidden && <Label size="small">Hidden</Label>}
                  {!account.includeInNetWorth && (
                    <Label size="small" basic>Excluded from Net Worth</Label>
                  )}
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
        {debtAccounts.length > 0 && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="3">
                <strong>Total Debt</strong>
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">
                <strong style={{ color: 'red' }}>
                  {formatCurrency(totalDebt)}
                </strong>
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Footer>
        )}
      </Table>
    </div>
  );
};

export default MonarchDebt;